import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
    fetchUsers as fetch,
    fetchSchools,
    loadUsers,
} from "../actions";
import api from "../../../../api/api";

const DEFAULT_FILTERS = {
    UserTypeId: 0,
    Sex: "",
    Country: "",
    SchoolId: "",
    SchoolName: "",
    IsActive: null,
    IsLocked: null,
    IsTeacher: null,
    CountryCustom: null,
    ActiveUntil: null,
    Name: "",
    Email: "",
    Username: "",
    Phone: "",
    ClassTypeId: 0,
    IsDeleted: false,
    Limit: 50,
    startActiveDate: "",
    endActiveDate: "",
};

/**
 * Custom hook encapsulating filter state, search sync, and lookup helpers.
 */
const useUserFilters = () => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const usersState = useSelector((state) => state.users);

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });

    // Initial load
    useEffect(() => {
        window.scrollToTop();
        dispatch(fetchSchools());
    }, [dispatch]);

    // Perform API search
    const handleSearchSync = useCallback(
        (customFilters) => {
            const f = customFilters || filters;
            dispatch(loadUsers());
            setTimeout(() => {
                dispatch(
                    fetch({
                        UserTypeId: f.UserTypeId,
                        SchoolId: f.SchoolId ? parseInt(f.SchoolId) : 0,
                        Country: f.Country || "",
                        IsActive: f.IsActive,
                        ActiveUntil: f.ActiveUntil,
                        CountryCustom: f.CountryCustom,
                        IsLocked: f.IsLocked,
                        IsTeacher: f.IsTeacher,
                        Sex: f.Sex,
                        startActiveDate: f.startActiveDate,
                        endActiveDate: f.endActiveDate,
                        IsDeleted: f.IsDeleted,
                        Email: f.Email,
                        Username: f.Username,
                        ClassTypeId: f.ClassTypeId,
                        Phone: f.Phone,
                        Name: f.Name,
                        Limit: f.Limit,
                    })
                );
            }, 500);
        },
        [filters, dispatch]
    );

    // Auto-search on mount
    useEffect(() => {
        handleSearchSync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClearFilters = useCallback(() => {
        const resetFilters = { ...DEFAULT_FILTERS };
        setFilters(resetFilters);
        handleSearchSync(resetFilters);
    }, [handleSearchSync]);

    // School search options for react-select async
    const promiseSchoolOptions = useCallback((inputValue, callback) => {
        api.get(`/Schools/?byView=true`)
            .then((resp) => {
                const list = resp.data?.data || [];
                const options = list.map(({ Id, Name }) => ({ label: Name, value: Id }));
                if (inputValue) {
                    const filtered = options.filter(opt => 
                        opt.label && opt.label.toLowerCase().includes(inputValue.toLowerCase())
                    );
                    callback(filtered);
                } else {
                    callback(options);
                }
            })
            .catch(() => callback([]));
    }, []);

    // Class room search options for react-select async
    const promiseClassRoomOptions = useCallback(
        (inputValue, callback, activeUser) => {
            if (!activeUser?.School?.Id) {
                callback([]);
                return;
            }
            api.post(`/ClassRoomsLookups/`, {
                Name: inputValue,
                SchoolId: activeUser.School.Id,
            })
                .then((resp) => {
                    if (resp.data.data?.length > 0) {
                        callback(
                            resp.data.data.map((p) => ({
                                label: p.ClassType.Name + " " + p.Division.Name,
                                value: p.Id,
                            }))
                        );
                    } else {
                        callback([]);
                    }
                })
                .catch(() => callback([]));
        },
        []
    );

    // Derived state
    const usersList = usersState?.users || [];
    const isUsersLoaded = usersState?.isLoaded || false;
    const toRemoveList = usersState?.toRemove || [];

    return {
        // Filter state
        search,
        setSearch,
        filters,
        setFilters,

        // Handlers
        handleSearchSync,
        handleClearFilters,
        promiseSchoolOptions,
        promiseClassRoomOptions,

        // Derived
        usersList,
        isUsersLoaded,
        toRemoveList,

        t,
    };
};

export default useUserFilters;
